/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { ModalAwareScrollView } from '../../../shared/modals/ModalAwareScrollView';
import { Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { webDarkScrollProps, webDarkScrollStyle } from '../../../shared/theme/webScrollbar';
import { styles } from '../posShared.styles';
import { PosCheckoutAlertsBand } from './PosCheckoutAlertsBand';
import { PosCheckoutDetailPanel } from './PosCheckoutDetailPanel';
import { PosCheckoutFiscalPanel } from './PosCheckoutFiscalPanel';
import { PosCheckoutPaymentPanel } from './PosCheckoutPaymentPanel';
import { PosCheckoutSummaryRail } from './PosCheckoutSummaryRail';
import { PosCheckoutTotalHero } from './PosCheckoutTotalHero';
import { PosCheckoutAttributionPanel } from './PosCheckoutAttributionPanel';
import { PosCheckoutTipChips } from './PosCheckoutTipChips';
import { MoneySummaryRow } from './MoneySummaryRow';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';
import { formatPosMoney } from '../../application/posMoneyFormat';

const STICKY_FOOTER_OFFSET_FALLBACK = 140;

type BodyProps = {
  visible: boolean;
  layoutWide: boolean;
  detailExpanded: boolean;
  onToggleDetail: () => void;
  fiscalExpanded: boolean;
  onToggleFiscal: () => void;
};

export function PosCheckoutBody({
  layoutWide,
  detailExpanded,
  onToggleDetail,
  fiscalExpanded,
  onToggleFiscal,
  visible,
}: BodyProps) {
  const ctx = usePosCheckoutContextOptional();
  const [stickyFooterHeight, setStickyFooterHeight] = useState(STICKY_FOOTER_OFFSET_FALLBACK);

  if (!ctx) return null;

  const {
    cashSessionOpen,
    onOpenCashManagement,
    cashManagementAccessBlockedHint,
    promoUiStatus,
    total,
    merchandiseTotal = total,
    tipAmount = 0,
    tipUiEnabled = false,
    tipIntent = null,
    onSelectTipIntent,
    cartEvaluation = null,
    saleFiscalMode,
    paymentDrafts,
    hasBlockingStockIssue,
    isSaving,
    onCompleteSale,
    onRequestStockCorrection,
  } = ctx;

  const tipBlock =
    tipUiEnabled && onSelectTipIntent ? (
      <View style={{ marginBottom: spacing.sm }}>
        <PosCheckoutTipChips
          merchandiseTotal={merchandiseTotal}
          tipIntent={tipIntent}
          onSelect={onSelectTipIntent}
        />
        {tipAmount > 0 ? (
          <View style={styles.checkoutFooterTotals}>
            <MoneySummaryRow
              label="Subtotal"
              value={formatPosMoney(merchandiseTotal)}
            />
            <MoneySummaryRow
              label="Propina"
              value={formatPosMoney(tipAmount)}
            />
            <Text style={styles.checkoutPayHeroHint}>Total incluye propina</Text>
          </View>
        ) : null}
      </View>
    ) : null;

  const detailPanel = (
    <PosCheckoutDetailPanel
      layoutWide={layoutWide}
      detailExpanded={detailExpanded}
      onToggleDetail={onToggleDetail}
    />
  );

  const fiscalPanel = (
    <PosCheckoutFiscalPanel
      collapsible={!layoutWide}
      fiscalExpanded={layoutWide || fiscalExpanded}
      onToggleFiscal={onToggleFiscal}
    />
  );

  const paymentPanel = (
    <>
      {tipBlock}
      <PosCheckoutPaymentPanel
        visible={visible}
        showSectionTitle
        collapseSecondaryFields={!layoutWide}
      />
    </>
  );

  if (layoutWide) {
    return (
      <View style={styles.checkoutModalBody}>
        <PosCheckoutAlertsBand
          cashSessionOpen={cashSessionOpen}
          onOpenCashManagement={onOpenCashManagement}
          cashManagementAccessBlockedHint={cashManagementAccessBlockedHint}
          promoUiStatus={promoUiStatus}
        />
        <View style={styles.checkoutWideColumns}>
          <View style={styles.checkoutTicketColumn}>
            <ModalAwareScrollView
              {...webDarkScrollProps()}
              style={webDarkScrollStyle}
              contentContainerStyle={styles.checkoutTicketColumnContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              bottomOffset={spacing.md}
            >
              {detailPanel}
              <PosCheckoutAttributionPanel layoutWide />
              {fiscalPanel}
            </ModalAwareScrollView>
          </View>
          <View style={styles.checkoutRailColumn}>
            <ModalAwareScrollView
              {...webDarkScrollProps()}
              style={[styles.checkoutRailScroll, webDarkScrollStyle]}
              contentContainerStyle={styles.checkoutRailScrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              bottomOffset={spacing.md}
            >
              {paymentPanel}
            </ModalAwareScrollView>
            <PosCheckoutSummaryRail
              variant="railEmbedded"
              showHero
              checkoutVisible={visible}
              total={total}
              totalIngresado={ctx.totalIngresado}
              saldoPendiente={ctx.saldoPendiente}
              vuelto={ctx.vuelto}
              paymentDrafts={paymentDrafts}
              cartEvaluation={cartEvaluation}
              saleFiscalMode={saleFiscalMode}
              hasBlockingStockIssue={hasBlockingStockIssue}
              isSaving={isSaving}
              onCompleteSale={onCompleteSale}
              onRequestStockCorrection={onRequestStockCorrection}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.checkoutModalBody, styles.checkoutModalBodyFill]}>
      <PosCheckoutAlertsBand
        cashSessionOpen={cashSessionOpen}
        onOpenCashManagement={onOpenCashManagement}
        cashManagementAccessBlockedHint={cashManagementAccessBlockedHint}
        promoUiStatus={promoUiStatus}
      />
      <ModalAwareScrollView
        {...webDarkScrollProps()}
        style={[styles.checkoutScroll, webDarkScrollStyle]}
        contentContainerStyle={styles.checkoutContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={spacing.md}
        extraKeyboardSpace={-stickyFooterHeight}
      >
        <View style={styles.checkoutHeroBlock}>
          <PosCheckoutTotalHero
            total={total}
            cartEvaluation={cartEvaluation}
            saleFiscalMode={saleFiscalMode}
            showFiscalHint={false}
            badgeText={paymentDrafts.length > 1 ? `${paymentDrafts.length} pagos` : undefined}
            checkoutVisible={visible}
          />
        </View>
        {paymentPanel}
        {detailPanel}
        <PosCheckoutAttributionPanel layoutWide={false} />
        {fiscalPanel}
      </ModalAwareScrollView>
      <View
        onLayout={(e) => {
          const next = Math.ceil(e.nativeEvent.layout.height);
          if (next > 0) setStickyFooterHeight(next);
        }}
      >
        <PosCheckoutSummaryRail
          variant="stickyFooter"
          checkoutVisible={visible}
          total={total}
          totalIngresado={ctx.totalIngresado}
          saldoPendiente={ctx.saldoPendiente}
          vuelto={ctx.vuelto}
          paymentDrafts={paymentDrafts}
          cartEvaluation={cartEvaluation}
          saleFiscalMode={saleFiscalMode}
          hasBlockingStockIssue={hasBlockingStockIssue}
          isSaving={isSaving}
          onCompleteSale={onCompleteSale}
          onRequestStockCorrection={onRequestStockCorrection}
        />
      </View>
    </View>
  );
}
